export const NEWS_LIST_PATH = '/about/news';
export const NEWS_LIST_BROWSE_PATH = `${NEWS_LIST_PATH}/browse`;

const NEWS_LIST_QUERY_KEYS = [
  'page',
  'order',
  'q',
  'category',
  'subcategory',
] as const;

type NewsListQueryKey = (typeof NEWS_LIST_QUERY_KEYS)[number];
type NewsListQuery = Partial<Record<NewsListQueryKey, string>>;

function firstString(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

export function parseNewsListPage(value: string | undefined) {
  if (!value || !/^\d+$/.test(value)) return null;

  const page = Number(value);
  return Number.isSafeInteger(page) && page > 0 ? page : null;
}

export function buildNewsListPagePath(page: number) {
  return page <= 1 ? NEWS_LIST_PATH : `${NEWS_LIST_PATH}/page/${page}`;
}

export function selectNewsListQuery(
  query: Record<string, string | string[] | undefined>,
): NewsListQuery {
  return Object.fromEntries(
    NEWS_LIST_QUERY_KEYS.flatMap((key) => {
      const value = firstString(query[key]);
      return value === undefined ? [] : [[key, value]];
    }),
  );
}

export function buildNewsListBrowsePath(query: NewsListQuery) {
  const search = new URLSearchParams();
  NEWS_LIST_QUERY_KEYS.forEach((key) => {
    const value = query[key];
    if (value) search.set(key, value);
  });
  const serialized = search.toString();

  return `${NEWS_LIST_BROWSE_PATH}${serialized ? `?${serialized}` : ''}`;
}

export function hasNewsListBrowseQuery(query: NewsListQuery) {
  return Boolean(
    query.q ||
      query.category ||
      query.subcategory ||
      (query.order && query.order !== 'order:ASC'),
  );
}

export function resolveLegacyNewsListDestination(search: URLSearchParams) {
  const query = selectNewsListQuery(Object.fromEntries(search.entries()));
  const requestedPage = parseNewsListPage(query.page);

  if (hasNewsListBrowseQuery(query)) {
    return buildNewsListBrowsePath({
      ...query,
      page: String(requestedPage ?? 1),
    });
  }

  if (!search.has('page')) return null;

  const remainingSearch = new URLSearchParams(search);
  remainingSearch.delete('page');
  const pathname = buildNewsListPagePath(requestedPage ?? 1);
  const serialized = remainingSearch.toString();

  return `${pathname}${serialized ? `?${serialized}` : ''}`;
}
